export const COLOR_PICKER_NO_COLOR = 'none'

export const isColorPickerNoColor = (value: string): boolean => {
  const v = value.trim().toLowerCase()
  return v === COLOR_PICKER_NO_COLOR || v === 'transparent'
}

export const normalizeIncomingValue = (value: string | undefined): string => {
  if (value === undefined || value === '') return '#CF5CC5'
  const trimmed = value.trim()
  const lower = trimmed.toLowerCase()
  if (lower === COLOR_PICKER_NO_COLOR || lower === 'transparent') {
    return COLOR_PICKER_NO_COLOR
  }
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
}

export const pickInitialColor = (includeNoColor: boolean | undefined, value: string | undefined): string => {
  if (includeNoColor) {
    return normalizeIncomingValue(value)
  }
  return value || '#CF5CC5'
}

export const hexLookupKey = (hex: string): string => hex.trim().toLowerCase()

export type SketchOnChangePayload = {
  hex: string
  rgb?: { r?: number; g?: number; b?: number; a?: number }
  hsv?: { h?: number; s?: number; v?: number }
  hsl?: { h?: number; s?: number; l?: number }
  source?: string
}

export function presetNoColorClick(color: SketchOnChangePayload): boolean {
  const h = typeof color.hex === 'string' ? color.hex.trim().toLowerCase() : ''
  return color.source === 'hex' && h === 'transparent'
}

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)))
}

function rgbTripletToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((c) => clamp255(c).toString(16).padStart(2, '0')).join('')}`
}

/** h in degrees (tinycolor / react-color), s,v in [0,1] */
function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const ss = Math.max(0, Math.min(1, s))
  const vv = Math.max(0, Math.min(1, v))
  const hh = ((((h % 360) + 360) % 360) / 360) * 6
  const i = Math.floor(hh)
  const f = hh - i
  const p = vv * (1 - ss)
  const q = vv * (1 - f * ss)
  const t = vv * (1 - (1 - f) * ss)
  let r = 0
  let g = 0
  let bl = 0
  switch (i % 6) {
    case 0:
      r = vv; g = t; bl = p; break
    case 1:
      r = q; g = vv; bl = p; break
    case 2:
      r = p; g = vv; bl = t; break
    case 3:
      r = p; g = q; bl = vv; break
    case 4:
      r = t; g = p; bl = vv; break
    default:
      r = vv; g = p; bl = q; break
  }
  return { r: clamp255(r * 255), g: clamp255(g * 255), b: clamp255(bl * 255) }
}

/** hsl from tinycolor: h° 0–360, s & l [0,1] */
function hslToRgb(hDeg: number, s: number, l: number): { r: number; g: number; b: number } {
  const sl = Math.max(0, Math.min(1, s))
  const ll = Math.max(0, Math.min(1, l))
  const hNorm = ((((hDeg % 360) + 360) % 360) / 360)

  function hue2rgb(p: number, q: number, t: number): number {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }

  if (sl === 0) {
    const v = clamp255(ll * 255)
    return { r: v, g: v, b: v }
  }
  const q = ll < 0.5 ? ll * (1 + sl) : ll + sl - ll * sl
  const p = 2 * ll - q
  const r = hue2rgb(p, q, hNorm + 1 / 3) * 255
  const g = hue2rgb(p, q, hNorm) * 255
  const b = hue2rgb(p, q, hNorm - 1 / 3) * 255
  return { r: clamp255(r), g: clamp255(g), b: clamp255(b) }
}

/**
 * Sketch often keeps rgb.a===0 with disableAlpha; chroma lives in rgb/hsv/hsl.
 * Prefer rgb channels, then hsv/hsl derived opaque hex — never downgrade slider moves to none.
 */
export function deriveOpaqueHexFromSketch(color: SketchOnChangePayload): string | null {
  const { rgb } = color
  if (
    rgb &&
    typeof rgb.r === 'number' &&
    typeof rgb.g === 'number' &&
    typeof rgb.b === 'number' &&
    !Number.isNaN(rgb.r) &&
    !Number.isNaN(rgb.g) &&
    !Number.isNaN(rgb.b)
  ) {
    return rgbTripletToHex(rgb.r, rgb.g, rgb.b)
  }
  const { hsv } = color
  if (
    hsv &&
    typeof hsv.h === 'number' &&
    typeof hsv.s === 'number' &&
    typeof hsv.v === 'number' &&
    !Number.isNaN(hsv.h) &&
    !Number.isNaN(hsv.s) &&
    !Number.isNaN(hsv.v)
  ) {
    const { r, g, b } = hsvToRgb(hsv.h, hsv.s, hsv.v)
    return rgbTripletToHex(r, g, b)
  }
  const { hsl } = color
  if (
    hsl &&
    typeof hsl.h === 'number' &&
    typeof hsl.s === 'number' &&
    typeof hsl.l === 'number' &&
    !Number.isNaN(hsl.h) &&
    !Number.isNaN(hsl.s) &&
    !Number.isNaN(hsl.l)
  ) {
    const { r, g, b } = hslToRgb(hsl.h, hsl.s, hsl.l)
    return rgbTripletToHex(r, g, b)
  }
  return null
}
